import {
  BarChartOutlined,
  CheckCircleOutlined,
  CloudOutlined,
  FileProtectOutlined,
  FireOutlined,
  GlobalOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { authLogin, authMe, authOtpRequest, authOtpVerify } from "@shared/api/generated/auth/auth";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import { parseApiError } from "@shared/lib/parseApiError";
import { persistAuthUser, setStoredUserEmail } from "@shared/userStorage";
import {
  App as AntApp,
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Grid,
  Input,
  List,
  Row,
  Segmented,
  Space,
  Steps,
  Typography,
  theme,
} from "antd";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

const BENEFITS = [
  {
    icon: FileProtectOutlined,
    title: "Автоматические отчеты 80020",
    description: "Без участия пользователя",
  },
  {
    icon: GlobalOutlined,
    title: "Работайте из любого места",
    description: "Веб-интерфейс 24/7",
  },
  {
    icon: SafetyOutlined,
    title: "Надежность данных",
    description: "Ежедневное резервирование",
  },
  {
    icon: BarChartOutlined,
    title: "Мощная аналитика",
    description: "Экспорт в Excel",
  },
];

const RESOURCES = [
  { icon: ThunderboltOutlined, label: "Электроэнергия" },
  { icon: FireOutlined, label: "Тепло" },
  { icon: CloudOutlined, label: "Вода" },
  { icon: FireOutlined, label: "Газ" },
];

const MetricaLogo: FC<{ size?: number; color?: string }> = ({ size = 40, color = "#1677ff" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Metrica Logo"
  >
    <circle cx="12" cy="12" r="10" fill={color} />
    <path
      d="M8 12.5C8.69036 12.5 9.25 13.0596 9.25 13.75V16.25C9.25 16.9404 8.69036 17.5 8 17.5C7.30964 17.5 6.75 16.9404 6.75 16.25V13.75C6.75 13.0596 7.30964 12.5 8 12.5Z"
      fill="white"
    />
    <path
      d="M12 9.5C12.6904 9.5 13.25 10.0596 13.25 10.75V16.25C13.25 16.9404 12.6904 17.5 12 17.5C11.3096 17.5 10.75 16.9404 10.75 16.25V10.75C10.75 10.0596 11.3096 9.5 12 9.5Z"
      fill="white"
    />
    <path
      d="M16 6.5C16.6904 6.5 17.25 7.05964 17.25 7.75V16.25C17.25 16.9404 16.6904 17.5 16 17.5C15.3096 17.5 14.75 16.9404 14.75 16.25V7.75C14.75 7.05964 15.3096 6.5 16 6.5Z"
      fill="white"
    />
  </svg>
);

const ProductInfo: FC = () => {
  const { token } = theme.useToken();

  return (
    <Flex vertical gap={token.marginLG}>
      <Typography.Title level={2} style={{ marginBottom: 0 }}>
        Метрика про - энерго
        <Typography.Text
          type="secondary"
          style={{ display: "block", fontSize: 16, fontWeight: 400 }}
        >
          Управление ресурсами в облаке
        </Typography.Text>
      </Typography.Title>

      <List
        dataSource={BENEFITS}
        renderItem={({ icon: Icon, title, description }) => (
          <List.Item style={{ padding: `${token.paddingSM}px 0`, border: "none" }}>
            <List.Item.Meta
              avatar={
                <Flex
                  align="center"
                  justify="center"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: token.borderRadius,
                    background: token.colorPrimaryBg,
                  }}
                >
                  <Icon style={{ color: token.colorPrimary, fontSize: 18 }} />
                </Flex>
              }
              title={title}
              description={description}
            />
          </List.Item>
        )}
      />

      <Flex gap={token.marginMD} wrap>
        {RESOURCES.map(({ icon: Icon, label }) => (
          <Flex key={label} align="center" gap={token.marginXS}>
            <Icon style={{ color: token.colorPrimary }} />
            <Typography.Text>{label}</Typography.Text>
          </Flex>
        ))}
      </Flex>

      <Card
        size="small"
        style={{
          background: token.colorPrimaryBg,
          borderColor: token.colorPrimaryBorder,
        }}
      >
        <Flex align="center" gap={token.marginSM}>
          <CheckCircleOutlined style={{ fontSize: 24, color: token.colorPrimary }} />
          <Typography.Text strong>30 дней бесплатно</Typography.Text>
        </Flex>
      </Card>
    </Flex>
  );
};

const LoginPage: FC = () => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [authMethod, setAuthMethod] = useState<"otp" | "password">("otp");
  const [step, setStep] = useState(0);
  const [pending, setPending] = useState(false);
  const [email, setEmail] = useState("");
  const [passwordForm] = Form.useForm();
  const navigate = useNavigate();

  // Редирект если уже авторизован (с валидацией токена)
  useEffect(() => {
    const accessToken = localStorage.getItem("acweb_access");
    if (!accessToken) return;

    const validateToken = async () => {
      try {
        await authMe({
          headers: { Authorization: `Bearer ${accessToken}` },
          timeout: 5000,
        });
        navigate("/dashboard", { replace: true });
      } catch (err) {
        const parsed = parseApiError(err);
        // Очищаем токены только при явной ошибке авторизации (401/403)
        // При сетевых ошибках/таймаутах оставляем токены — пользователь увидит форму логина
        if (parsed.status === 401 || parsed.status === 403) {
          localStorage.removeItem("acweb_access");
          localStorage.removeItem("acweb_refresh");
        }
      }
    };
    validateToken();
  }, [navigate]);

  const handlePasswordLogin = async () => {
    try {
      const { email: inputEmail, password } = await passwordForm.validateFields();
      setPending(true);
      const res = await authLogin({ email: inputEmail.toLowerCase(), password });
      localStorage.setItem("acweb_access", res.tokens.access_token);
      localStorage.setItem("acweb_refresh", res.tokens.refresh_token);
      persistAuthUser(res.user);
      message.success("Добро пожаловать!");
      navigate("/dashboard");
    } catch (err) {
      const parsed = parseApiError(err);
      message.error(
        parsed.status === 401
          ? "Неверный email или пароль"
          : parsed.status === 429
            ? "Слишком много попыток, попробуйте позже"
            : parsed.message,
      );
    } finally {
      setPending(false);
    }
  };

  const handleRequest = async () => {
    try {
      const { email: inputEmail } = await form.validateFields(["email"]);
      setPending(true);
      await authOtpRequest({ email: inputEmail.toLowerCase() });
      setEmail(inputEmail.toLowerCase());
      setStep(1);
      message.success("Код отправлен на почту");
    } catch (err) {
      message.error(extractErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const handleVerify = async (codeOverride?: string) => {
    try {
      let code = codeOverride;
      if (!code) {
        const values = await form.validateFields(["code"]);
        code = values.code as string;
      }
      if (!code) return;
      setPending(true);
      const res = await authOtpVerify({ email, code: code.trim() });
      localStorage.setItem("acweb_access", res.tokens.access_token);
      localStorage.setItem("acweb_refresh", res.tokens.refresh_token);
      persistAuthUser(res.user);
      const hasEmailContact =
        Array.isArray(res.user.contacts) &&
        res.user.contacts.some(
          (c: unknown) =>
            typeof c === "object" && c !== null && (c as Record<string, unknown>).type === "email",
        );
      if (!hasEmailContact) setStoredUserEmail(email);
      message.success("Добро пожаловать!");
      navigate("/dashboard");
    } catch (err) {
      message.error(extractErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <Flex
      vertical
      justify="center"
      align="center"
      style={{
        minHeight: "100vh",
        background: token.colorBgLayout,
        padding: token.padding,
      }}
    >
      <Row
        gutter={[48, 32]}
        align="middle"
        justify="center"
        style={{ maxWidth: 1100, width: "100%" }}
      >
        <Col xs={24} sm={24} md={20} lg={10} xl={9}>
          <Card style={{ maxWidth: 420, margin: "0 auto" }}>
            <Space orientation="vertical" size="large" style={{ width: "100%" }}>
              <Flex vertical align="center" gap={8}>
                <MetricaLogo size={48} color={token.colorPrimary} />
                <Typography.Title level={3} style={{ margin: 0 }}>
                  Авторизоваться
                </Typography.Title>
              </Flex>

              <Divider style={{ margin: `${token.marginSM}px 0` }} />

              <Segmented
                block
                value={authMethod}
                onChange={(val) => setAuthMethod(val as "otp" | "password")}
                options={[
                  { label: "По email-коду", value: "otp" },
                  { label: "По паролю", value: "password" },
                ]}
              />

              {authMethod === "otp" ? (
                <>
                  <Steps
                    current={step}
                    items={[{ title: "Email" }, { title: "Код" }]}
                    size="small"
                  />

                  <Form form={form} layout="vertical">
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: "Введите email" },
                        { type: "email", message: "Некорректный email" },
                      ]}
                    >
                      <Input placeholder="user@example.com" disabled={step === 1} size="large" />
                    </Form.Item>
                    {step === 1 && (
                      <Form.Item
                        name="code"
                        label="Код"
                        rules={[{ required: true, message: "Введите код" }]}
                        extra="Код отправлен на почту"
                      >
                        <Input
                          placeholder="000000"
                          maxLength={6}
                          size="large"
                          style={{ letterSpacing: 8, textAlign: "center" }}
                          onChange={(e) => {
                            const value = e.target.value.trim();
                            if (value.length === 6 && /^\d{6}$/.test(value) && !pending) {
                              handleVerify(value);
                            }
                          }}
                        />
                      </Form.Item>
                    )}
                  </Form>

                  {step === 0 ? (
                    <Button
                      type="primary"
                      block
                      size="large"
                      onClick={handleRequest}
                      loading={pending}
                    >
                      Отправить код
                    </Button>
                  ) : (
                    <Space orientation="vertical" style={{ width: "100%" }} size="small">
                      <Button
                        type="primary"
                        block
                        size="large"
                        onClick={() => handleVerify()}
                        loading={pending}
                      >
                        Войти
                      </Button>
                      <Button
                        type="link"
                        block
                        onClick={() => {
                          setStep(0);
                          form.resetFields(["code"]);
                        }}
                      >
                        Изменить email
                      </Button>
                    </Space>
                  )}
                </>
              ) : (
                <>
                  <Form form={passwordForm} layout="vertical">
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: "Введите email" },
                        { type: "email", message: "Некорректный email" },
                      ]}
                    >
                      <Input placeholder="user@example.com" size="large" />
                    </Form.Item>
                    <Form.Item
                      name="password"
                      label="Пароль"
                      rules={[{ required: true, message: "Введите пароль" }]}
                    >
                      <Input.Password placeholder="Пароль" size="large" />
                    </Form.Item>
                  </Form>
                  <Button
                    type="primary"
                    block
                    size="large"
                    onClick={handlePasswordLogin}
                    loading={pending}
                  >
                    Войти
                  </Button>
                </>
              )}
            </Space>
          </Card>
        </Col>

        {screens.lg && (
          <Col lg={14} xl={15}>
            <ProductInfo />
          </Col>
        )}
      </Row>

      <Typography.Link
        href="https://metrica.pro/"
        target="_blank"
        rel="noopener noreferrer"
        type="secondary"
        style={{ marginTop: token.marginXL }}
      >
        {new Date().getFullYear()} metrica.pro
      </Typography.Link>
    </Flex>
  );
};

export default LoginPage;
